import { useState, useEffect } from "react";
import { Row, Col, Space, message, Table } from "antd";
import { ReButton, ReModal } from "@/components/refineElement";
import { getLangList } from '@/lib/i18n';
import DepartmentConfig from "@/config/department";
import RoleConfig from "@/config/role";
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import { hasPermission } from "@/lib/permissionUtil";
import { DepartmentType, RoleType, RoleListType } from './types';
import Roles from './Roles';
import ModalDepartment from './ModalDepartment';
import ModalRole from './ModalRole';

const { getDepartmentInfo, createDepartmentInfo, updateDepartmentInfo, deleteDepartmentInfo } = DepartmentConfig;
const { getRoleInfo, createRoleInfo, updateRoleInfo, deleteRoleInfo } = RoleConfig;

type Props = {
  setSelectedRoleInfo: Function;
}

function Departments(props: Props) {
  const { setSelectedRoleInfo } = props;
  const [departmentArray, setDepartmentArray] = useState<DepartmentType[]>([]);
  const [departmentInfo, setDepartmentInfo] = useState({} as DepartmentType);
  const [roleList, setRoleList] = useState<RoleListType[]>([]);
  const [roleInfo, setRoleInfo] = useState({} as RoleType);
  const [handleType, setHandleType] = useState('');
  const [isModalDepartmentVisible, setIsModalDepartmentVisible] = useState(false);
  const [isModalRoleVisible, setIsModalRoleVisible] = useState(false);
  const organizationT: any = getLangList("organization");
  const buttonT: any = getLangList("button");
  const errorT: any = getLangList("error");
  const okDepartmentFunction = {
    create: createDepartmentInfoFunction,
    update: updateDepartmentInfoFunction,
  };
  const okRoleFunction = {
    create: createRoleInfoFunction,
    update: updateRoleInfoFunction,
  };

  const columns = [
    { title: organizationT.DEPARTMENT, dataIndex: "departmentName" },
    {
      title: buttonT.OPERATE,
      dataIndex: "action",
      render: (text: any, record: any) => (hasPermission('ORGANIZATION_PERMISSION_MANAGEMENT_EDIT') && record.id !== 1 && // id: 1為學生部門 固定不能修改跟刪除
        <Space>
          <ReButton iconName='edit' onClick={clickDepartmentEdit(record)} attributeObject={{ size: "small" }}>
            {buttonT.EDIT}
          </ReButton>

          <ReButton iconName='delete' onClick={clickDepartmentDelete(record.id)} attributeObject={{ type: 'danger', size: "small" }}>
            {buttonT.DELETE}
          </ReButton>
        </Space>
      ),
    }
  ];

  useEffect(() => {
    getDepartmentInfoFunction();
    getRoleInfoFunction();
  }, []);

  function getDepartmentInfoFunction() {
    getDepartmentInfo().then((res: any) => {
      const departmentArray = res?.value ?? [];
      setDepartmentArray(departmentArray);
    });
  };

  function getRoleInfoFunction() {
    getRoleInfo().then((res: any) => {
      const roleList = res?.value ?? [];
      setRoleList(roleList);
    });
  };

  function toggleDepartmentModal(handleType: string = '') {
    setIsModalDepartmentVisible(!isModalDepartmentVisible);
    setHandleType(handleType);
  };

  function toggleRoleModal(handleType: string = '') {
    setIsModalRoleVisible(!isModalRoleVisible);
    setHandleType(handleType);
  };

  function clickDepartmentCreate() {
    setDepartmentInfo({} as DepartmentType);
    toggleDepartmentModal('create');
  };

  const clickDepartmentEdit = (data: DepartmentType) => (e: any) => {
    e.stopPropagation();
    setDepartmentInfo(data);
    toggleDepartmentModal('update');
  };

  const clickDepartmentDelete = (id: number) => () => {
    showDelConfirm(() => deleteDepartmentInfoFunction(id));
  };

  function clickRoleCreate() {
    setRoleInfo({} as RoleType);
    toggleRoleModal('create');
  };

  const clickRoleEdit = (data: RoleType) => (e: any) => {
    e.stopPropagation();
    setRoleInfo(data);
    toggleRoleModal('update');
  };

  const clickRoleDelete = (id: number) => () => {
    showDelConfirm(() => deleteRoleInfoFunction(id));
  };

  function createDepartmentInfoFunction(values: DepartmentType) {
    const { departmentName } = values;
    const params = { departmentName };

    createDepartmentInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleDepartmentModal();
      getDepartmentInfoFunction();
    });
  };

  function updateDepartmentInfoFunction(values: DepartmentType) {
    const { id } = departmentInfo;
    const { departmentName } = values;
    const params = { departmentName, id };

    updateDepartmentInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleDepartmentModal();
      getDepartmentInfoFunction();
    });
  }

  function deleteDepartmentInfoFunction(id: number) {
    const params = { id };

    deleteDepartmentInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getDepartmentInfoFunction();
    });
  };

  function createRoleInfoFunction(values: RoleType) {
    const { roleDesc, departmentId } = values;
    const params = { roleDesc, departmentId };

    createRoleInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleRoleModal();
      getRoleInfoFunction();
    });
  };

  function updateRoleInfoFunction(values: RoleType) {
    const { roleId } = roleInfo;
    const { roleDesc, departmentId } = values;
    const params = { roleDesc, departmentId, roleId };

    updateRoleInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleRoleModal();
      getRoleInfoFunction();
    });
  }

  function deleteRoleInfoFunction(id: number) {
    const params = { roleId: id };

    deleteRoleInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getRoleInfoFunction();
    });
  };

  return (
    <>
      <Row gutter={[0, 8]} justify="end">
        <Col>
          {hasPermission('ORGANIZATION_PERMISSION_MANAGEMENT_EDIT') &&
            <Space>
              <ReButton iconName='plus' onClick={clickDepartmentCreate}>
                {`${buttonT.NEW}${organizationT.DEPARTMENT}`}
              </ReButton>

              <ReButton iconName='plus' onClick={clickRoleCreate}>
                {`${buttonT.NEW}${organizationT.ROLE}`}
              </ReButton>
            </Space>
          }
        </Col>
        <Col span={24}>
          <Table
            dataSource={departmentArray}
            columns={columns}
            pagination={false}
            rowKey="id"
            size="small"
            expandable={{
              expandedRowRender: (record: DepartmentType) => (
                <Roles
                  clickRoleEdit={clickRoleEdit}
                  clickRoleDelete={clickRoleDelete}
                  roleArray={roleList.find((item: RoleListType) => item.departmentId === record.id)?.roleList ?? []}
                  setSelectedRoleInfo={setSelectedRoleInfo}
                />
              )
            }}
          />
        </Col>
      </Row>

      {isModalDepartmentVisible &&
        <ReModal
          title={organizationT.DEPARTMENT}
          onCancel={() => toggleDepartmentModal()}
          visible={isModalDepartmentVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okDepartmentFunction)}
          CollectedForm={ModalDepartment}
          formProps={{
            handleType,
            info: departmentInfo,
          }}
        />
      }

      {isModalRoleVisible &&
        <ReModal
          title={organizationT.ROLE}
          onCancel={() => toggleRoleModal()}
          visible={isModalRoleVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okRoleFunction)}
          CollectedForm={ModalRole}
          formProps={{
            handleType,
            info: roleInfo,
            departmentArray: departmentArray,
          }}
        />
      }
    </>
  );
}

export default Departments;